import React from 'react';

import styles from './userReferralsShowPageStyles.scss';

import Button, { BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import Link from '@buyside/components/presentation/Link/link';
import ReferralMethodologyModal from '@buyside/components/presentation/UserAccount/YourReferrals/referralMethodologyModal';
import SocialShare from '@buyside/components/presentation/SocialShare/socialShare';

import { useCurrentUserAccountUserReferralsQuery } from '@interfaces/graphql-typings';

import LoadingSpinner, {
  SIZES
} from '@buyside/components/presentation/LoadingSpinner/loadingSpinner';

import { inviteMembersUrl, profileUrl } from '@buyside/util/urlHelper';

const UserReferralsShowPage = (): React.ReactElement => {
  const { loading, data } = useCurrentUserAccountUserReferralsQuery();

  if (loading || !data?.currentUser) return <LoadingSpinner size={SIZES.LARGE} />;

  const { allTimeReferralAwards, id, userReferrals, unregisteredReferrals } = data.currentUser;

  return (
    <>
      <div className={styles.pageTitleContainer}>
        <div className={styles.pageTitle}>Your Referrals</div>
        <div className={styles.inviteButton}>
          <Button text="Invite New Members" type={BUTTON_TYPES.PRIMARY} url={inviteMembersUrl()} />
        </div>
      </div>
      <div className={styles.awardDayBox}>
        You've earned {allTimeReferralAwards ? allTimeReferralAwards : 0} days of all-time free
        access from your referrals.
        <div className={styles.inviteButton}>
          <Button text="Invite New Members" type={BUTTON_TYPES.PRIMARY} url={inviteMembersUrl()} />
        </div>
      </div>
      <div>
        <p>
          When you refer a qualified candidate that is accepted to SumZero, you get free access. If
          a candidate you refer posts an idea, you get even more! <ReferralMethodologyModal />
        </p>
        <p>
          Share your referral link on social media with friends and colleagues to start getting free
          access.
        </p>
        <strong>Your referral link:</strong>
        <SocialShare id={id} />
      </div>
      {userReferrals ? (
        <div className={styles.referralContainer}>
          <h4>Referrals</h4>
          <table>
            <tbody>
              <tr>
                <th>Member</th>
                <th>Milestones</th>
                <th>Bonus Access Days</th>
              </tr>
              {userReferrals.map(ref => {
                if (!ref || !ref.referralProgramMaxRewardDays) return null;
                const { referee, cumulativeRewardDays, referralProgramMaxRewardDays } = ref;
                return (
                  <tr key={ref.id}>
                    <td>
                      <Link to={profileUrl(referee.id)} className={styles.refereeLink}>
                        {referee.displayName}
                      </Link>
                      <div>
                        (
                        <Link to={`mailto:${referee.email}`} className={styles.refereeLink}>
                          {referee.email}
                        </Link>
                        )
                      </div>
                    </td>
                    <td>
                      <div className={styles.progressBar}>
                        <div
                          className={styles.progressBarSegment}
                          style={{
                            width:
                              Math.floor(
                                ((cumulativeRewardDays || 0) / referralProgramMaxRewardDays) * 100
                              ) + '%'
                          }}
                        />
                      </div>
                      <div className={styles.progressMilestoneLabels}>
                        <small>Joined SumZero</small>
                        <small>1st Idea Posted</small>
                        <small>2nd Idea Posted</small>
                      </div>
                    </td>
                    <td className={styles.totalDays}>{cumulativeRewardDays}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {unregisteredReferrals?.length ? (
        <div className={styles.referralContainer}>
          <h4>Unregistered Referrals</h4>
          <p>
            We've accepted the following referral(s) to SumZero, but they have not completed our
            two-minute registration process. To obtain free access, ensure your referrals
            successfully finish registering for SumZero:
          </p>
          <ul>
            {unregisteredReferrals.map(app => {
              return (
                <li key={app?.id}>
                  <Link to={`mailto:${app?.email}`} className={styles.refereeLink}>
                    {app?.email}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </>
  );
};

export default UserReferralsShowPage;
